/**
 * caseStudyAskSuppression — one-time-per-user dismiss for the Case-Study-
 * Engine-Hook testimonial ask (modules-loop-b.md "Case-Study-Engine-Hook",
 * "nach 1. Pilot: Testimonial-Ask ... Rolle: Daniel + kleiner FE-Trigger").
 *
 * Persists the dismiss in `localStorage`, KEYED PER UID (deviation from the
 * spec's literal example key name `caseStudyAskDismissed`; documented in
 * changelog.md) so that dismissing on a shared/test browser does not
 * silently suppress the ask for a different account that later signs in on
 * the same device — the spec explicitly allows either `localStorage` or
 * `Firestore` for this flag, and per-uid localStorage keeps the "no new
 * backend infra" simplicity while avoiding cross-account bleed.
 *
 * Best-effort/fail-open, same rationale as
 * `advisorInviteCtaSuppression`/`paywallTrigger4Suppression`: this is a
 * one-time UX nudge, not a security control.
 *
 * @module utils/caseStudyAskSuppression
 */

function storageKey(uid: string): string {
  return `faebill_case_study_ask_dismissed_${uid}`;
}

/**
 * `true` if `uid` has already dismissed (or already seen and acted on) the
 * testimonial ask. Fail-open: any storage read error resolves to `false`.
 *
 * @param uid - the current user's Firebase Auth uid.
 */
export function isCaseStudyAskDismissed(uid: string): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(storageKey(uid)) === '1';
  } catch {
    return false;
  }
}

/**
 * Marks the testimonial ask as dismissed for `uid` — permanent, this is a
 * ONE-TIME ask (module doc: "einmalig"), never re-shown after either an
 * explicit dismiss or a click-through. Best-effort: a storage write failure
 * is silently ignored.
 *
 * @param uid - the current user's Firebase Auth uid.
 */
export function dismissCaseStudyAsk(uid: string): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(storageKey(uid), '1');
  } catch {
    // best-effort — ignore.
  }
}
